import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  MenuItem,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { FiFile } from 'react-icons/all';
import { FiX } from 'react-icons/fi';
import axios, { AxiosRequestConfig } from 'axios';
import { queryClient } from '~/utils/react-query';
import { STORAGE } from '~/constants';
import { Input, ReactSelect, Select, Textarea } from '~/components';
import { SelectItem } from '~/types';
import {
  useCompanyGroupsAsSelect,
  useContractsForDocuments,
  useFilialAsSelect,
  useMatrizAsSelect,
  useRoleCan,
} from '~/hooks';
import api from '~/utils/api';
import { useDocumentsTypesAsSelect } from '~/hooks/useDocuments/useDocuments';
import { Documents } from '~/types/documents';
import { TypeForm } from '~/pages/Portal/Documents/TypeForm';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import { getBranches, getHeadOffices } from '~/services/Filter';

export const DocumentsForm: React.FC<{
  isEdit: boolean;
  documents?: Documents;
  clearSelected?: () => void;
}> = ({ clearSelected, isEdit, documents }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [selectedContracts, setSelectedContracts] = useState<SelectItem[]>([]);
  const [contratos, setContracts] = useState<SelectItem[]>([]);
  const [isFetching, setFetching] = useState(false);
  const [nomeArquivo, setNomeArquivo] = useState<string | undefined>('');

  const [nome, setNome] = useState<string>('');
  const [observacoes, setObservacoes] = useState<string>('');
  const [arquivo, setSelectedFile] = useState<any>();
  const [id_tipo, setId_tipo] = useState<number | undefined>(undefined);
  const [id_grupoeconomico, setGroup] = useState<SelectItem | undefined>(
    undefined,
  );
  const [id_matriz, setMatriz] = useState<SelectItem | undefined>(undefined);
  const [id_filial, setFilial] = useState<SelectItem | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busca_contrato, setBuscaContrato] = useState<string>('');

  const user = JSON.parse(localStorage.getItem(`${STORAGE}:user`) || '');

  const { groups } = useContext(FilterContext);
  const { headOffices } = useContext(FilterContext);
  const { branches } = useContext(FilterContext);

  const { data: groupsSelected, isLoading: isGroupsLoading } =
    useCompanyGroupsAsSelect({}, 9999, groups);
  const {
    data: matrizSelecionada,
    isLoading: isMatrizLoading,
    refetch: refetchMatriz,
  } = useMatrizAsSelect(
    {},
    headOffices,
    id_grupoeconomico ? [id_grupoeconomico] : [],
  );
  const {
    data: filialSelecionada,
    isLoading: isFilialLoading,
    refetch: refetchFilial,
  } = useFilialAsSelect(
    {},
    branches,
    id_grupoeconomico ? [id_grupoeconomico] : [],
    headOffices.concat(id_matriz ?? []),
  );

  const { data: types, isLoading: isTypeLoading } = useDocumentsTypesAsSelect();

  const {
    data: contracts,
    isLoading: isContractsLoading,
    refetch,
  } = useContractsForDocuments(
    busca_contrato,
    id_grupoeconomico ? id_grupoeconomico?.value : '',
    id_matriz ? id_matriz?.value : '',
    id_filial ? id_filial?.value : '',
    {
      onSuccess: r => {
        setContracts(
          r.map((c: any) => ({
            label: c.nu_contrato,
            value: c.id_contrato,
          })),
        );
      },
    },
  );

  useEffect(() => {
    refetch();
  }, [id_grupoeconomico, busca_contrato, id_matriz, id_filial]);

  const handleOpen = () => {
    if (isEdit && documents) {
      const c = documents.contratos.map(it => {
        return {
          value: it.id_contrato as any,
          label: it.nu_contrato as any,
        };
      });
      setSelectedContracts(c);
      const idgrupo = groupsSelected?.find(it => {
        return it.label === documents?.nm_grupoeconomico;
      });
      setGroup(idgrupo);
      idgrupo && loadMatrizFilial(idgrupo);
      const idtipo = types?.find(it => {
        return it.label === documents?.nome_tipo;
      });
      setId_tipo(Number(idtipo?.value));
      setNome(documents?.nome || '');
      setNomeArquivo(documents?.nm_arquivo || '');
      setObservacoes(documents?.observacoes || '');
    }
    onOpen();
  };

  const loadMatrizFilial = async (grupo: SelectItem) => {
    let idmatriz: SelectItem | undefined;
    if (documents?.cnpj_matriz) {
      getHeadOffices({ groups: grupo.value }).then(m => {
        idmatriz = m?.find(it => {
          return it.label.includes(documents.cnpj_matriz.replace(/\./g, ''));
        });
        idmatriz && setMatriz(idmatriz);
      });
    }
    if (documents?.cnpj_filial) {
      getBranches({
        groups: grupo.value,
        ...(idmatriz ? { headOffices: idmatriz.value } : {}),
      }).then(f => {
        const idfilial = f?.find(it => {
          return it.label.includes(documents.cnpj_filial.replace(/\./g, ''));
        });
        idfilial && setFilial(idfilial);
      });
    }
  };
  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setNome('');
    setId_tipo(undefined);
    setSelectedContracts([]);
    setObservacoes('');
    setGroup(undefined);
    setSelectedFile(null);
    setNomeArquivo('');
    setMatriz(undefined);
    setFilial(undefined);
    clearErrors();
  };

  async function handleCreate() {
    clearErrors();

    const ct = selectedContracts.map(c => {
      return c.value;
    });

    !(arquivo || nomeArquivo) &&
      setError('arquivo', {
        type: 'required',
        message: 'Arquivo é obrigatório',
      });

    (!nome || !nome.trim().length) &&
      setError('nome', {
        type: 'required',
        message: 'Nome do documento é obrigatório',
      });

    nome.length > 60 &&
      setError('nome', {
        type: 'maxLength',
        message: 'Nome com mais de 60 caracteres',
      });

    observacoes.length > 255 &&
      setError('observacoes', {
        type: 'maxLength',
        message: 'Observações com mais de 255 caracteres',
      });

    !id_tipo &&
      setError('id_tipo', { type: 'required', message: 'Tipo é obrigatório' });

    !id_grupoeconomico?.value &&
      setError('id_grupoeconomico', {
        type: 'required',
        message: 'Grupo é obrigatório',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = new FormData();
    form.append('nome', nome);
    form.append('contratos', JSON.stringify(ct));
    if (arquivo) {
      form.append('arquivo', arquivo);
    }
    form.append('observacoes', observacoes);
    if (id_tipo) {
      form.append('id_tipo', id_tipo.toString());
    }
    if (id_grupoeconomico) {
      form.append('id_grupoeconomico', id_grupoeconomico.value);
    }
    if (id_matriz) {
      form.append('id_matriz', id_matriz.value);
    }
    if (id_filial) {
      form.append('id_filial', id_filial.value);
    }

    setFetching(true);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    let request;
    if (!isEdit) {
      request = api.post(`documento`, form, config);
    } else {
      request = api.put(`documento/${documents?.id_documento}`, form, config);
    }

    request
      .then(() => {
        toast({
          title: `Documento ${isEdit ? 'editado' : 'adicionado'}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        clearSelected && clearSelected();
        onClose();
        queryClient.invalidateQueries('documentsList');
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          // erros de valid

          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            toast({
              title: `Erros ao criar documento`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao ${isEdit ? 'editar' : 'adicionar'} documento`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  const handleSelectedContractsChange = (items?: SelectItem[]) => {
    setSelectedContracts(items || []);
  };

  const handleBuscaContratoChange = (texto?: string) => {
    setBuscaContrato(texto || '');
  };

  const handleFileChange = (_file: File | null) => {
    setSelectedFile(_file);
    setNomeArquivo(_file ? _file.name : '');
    createSuggestion(_file?.name);
    clearErrors('arquivo');
    clearErrors('nome');
  };

  function createSuggestion(fileName?: string) {
    const auxFileName = fileName
      ?.split('.')[0]
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .split(/-|_/);

    const finalName = auxFileName?.join(' ');

    setNome(finalName || '');
  }
  const clearFile = () => {
    setSelectedFile(null);
    setNomeArquivo('');
    setValue('arquivo', null);
  };

  function handleNome(e: any) {
    setNome(e.target.value);
    clearErrors('nome');
  }

  function handleObservacoes(e: any) {
    setObservacoes(e.target.value);
    if (e.target.value.length <= 255) {
      clearErrors('observacoes');
    }
  }

  function handleType(e: any) {
    setId_tipo(e.target.value);
    if (e.target.options.selectedIndex !== 0) {
      clearErrors('id_tipo');
    }
  }

  function handleGroup(e?: SelectItem) {
    setGroup(e);
    if (Number(e?.value) !== 0) {
      clearErrors('id_grupoeconomico');
    }
    setSelectedContracts([]);
    handleMatriz(undefined);
  }

  function handleMatriz(e?: SelectItem) {
    setMatriz(e);
    if (Number(e?.value) !== 0) {
      clearErrors('id_matriz');
    }
    setSelectedContracts([]);
    handleFilial(undefined);
    refetchFilial();
  }

  function handleFilial(e?: SelectItem) {
    setFilial(e);
    if (Number(e?.value) !== 0) {
      clearErrors('id_filial');
    }
    setSelectedContracts([]);
  }

  const hasPermission = useRoleCan({ roles: ['administrador', 'operacional'] });

  const modoVisualizacao = () => {
    return isEdit && (!hasPermission || documents?.modulo !== 'Contrato');
  };

  return (
    <>
      {!isEdit && (
        <Button
          color="white"
          background="gray.900"
          _hover={{
            bgColor: 'black',
          }}
          onClick={handleOpen}
        >
          Adicionar
        </Button>
      )}
      {isEdit && (
        <MenuItem onClick={handleOpen}>
          {modoVisualizacao() ? 'Visualizar' : 'Editar'}
        </MenuItem>
      )}
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="md"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader>
            {isEdit
              ? modoVisualizacao()
                ? 'Visualizar Documento'
                : 'Editar Documento'
              : 'Adicionar Documento'}
          </DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={400} w="100%">
              <Stack gridGap={4}>
                <FormControl isInvalid={errors.arquivo}>
                  <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                    Documento *
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiFile} />
                    </InputLeftElement>
                    {(arquivo || nomeArquivo) && (
                      <InputRightElement pointerEvents="auto">
                        <Icon
                          as={FiX}
                          style={{
                            cursor: 'pointer',
                            pointerEvents:
                              isFetching || modoVisualizacao()
                                ? 'none'
                                : 'auto',
                          }}
                          onClick={clearFile}
                        />
                      </InputRightElement>
                    )}
                    <input
                      id="file"
                      type="file"
                      onChange={e => {
                        handleFileChange(
                          e.target.files ? e.target.files[0] : null,
                        );
                        e.target.value = '';
                      }}
                      accept=".pdf"
                      ref={inputRef}
                      style={{ display: 'none' }}
                    />
                    <Input
                      placeholder="Selecione um arquivo"
                      onClick={() =>
                        inputRef.current ? inputRef.current.click() : ''
                      }
                      value={nomeArquivo || ''}
                      name="arquivo"
                      style={{ paddingLeft: '40px', paddingRight: '40px' }}
                      isReadOnly
                      error={errors.arquivo}
                      isDisabled={isFetching || modoVisualizacao()}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl isInvalid={errors.nome}>
                  <Input
                    type="text"
                    label="Nome do documento *"
                    error={errors.nome}
                    fontSize="15px"
                    value={nome}
                    name="nome"
                    onChange={e => handleNome(e)}
                    isDisabled={isFetching || modoVisualizacao()}
                  />
                </FormControl>
                <FormControl isInvalid={errors.id_tipo}>
                  <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                    Tipo *
                  </FormLabel>
                  <InputGroup>
                    <Select
                      name="id_tipo"
                      placeholder="Selecione"
                      error={errors.id_tipo}
                      onChange={e => handleType(e)}
                      fontSize="15px"
                      value={id_tipo}
                      isDisabled={isFetching || modoVisualizacao()}
                    >
                      {!isTypeLoading &&
                        types?.map(tipo => (
                          <option key={tipo.value} value={tipo.value}>
                            {tipo.label}
                          </option>
                        ))}
                    </Select>
                    {!modoVisualizacao() && <TypeForm />}
                  </InputGroup>
                </FormControl>
                <FormControl isInvalid={errors.id_grupoeconomico}>
                  <ReactSelect
                    name="id_grupoeconomico"
                    isSearchable
                    label="Grupo econômico *"
                    placeholder="Selecione"
                    error={errors.id_grupoeconomico}
                    options={groupsSelected}
                    value={id_grupoeconomico}
                    onChange={e => handleGroup(e as SelectItem)}
                    defaultStyle
                    isDisabled={isFetching || modoVisualizacao()}
                    defaultValue={
                      !isEdit
                        ? undefined
                        : groupsSelected?.length === 1
                        ? groupsSelected[0].value
                        : undefined
                    }
                  />
                </FormControl>
                {id_grupoeconomico?.value && (
                  <FormControl isInvalid={errors.id_matriz}>
                    <ReactSelect
                      name="id_matriz"
                      isSearchable
                      label="Matriz"
                      placeholder="Selecione"
                      error={errors.id_matriz}
                      options={matrizSelecionada}
                      value={id_matriz ?? null}
                      onChange={e => handleMatriz(e as SelectItem)}
                      defaultStyle
                      isDisabled={
                        isFetching || isMatrizLoading || modoVisualizacao()
                      }
                      defaultValue={
                        !isEdit
                          ? undefined
                          : matrizSelecionada?.length === 1
                          ? matrizSelecionada[0].value
                          : undefined
                      }
                      isClearable
                      isLoading={isMatrizLoading}
                      noOptionsText="Não há opções"
                    />
                  </FormControl>
                )}
                {id_grupoeconomico?.value && (
                  <FormControl isInvalid={errors.id_filial}>
                    <ReactSelect
                      name="id_filial"
                      isSearchable
                      label="Filial"
                      placeholder="Selecione"
                      error={errors.id_filial}
                      options={filialSelecionada}
                      value={id_filial ?? null}
                      onChange={e => handleFilial(e as SelectItem)}
                      defaultStyle
                      isDisabled={
                        isFetching || isFilialLoading || modoVisualizacao()
                      }
                      defaultValue={
                        !isEdit
                          ? undefined
                          : filialSelecionada?.length === 1
                          ? filialSelecionada[0].value
                          : undefined
                      }
                      isClearable
                      isLoading={isFilialLoading}
                      noOptionsText="Não há opções"
                    />
                  </FormControl>
                )}
                {id_grupoeconomico?.value && (
                  <ReactSelect
                    name="contratos"
                    isMulti
                    isSearchable
                    label="Contratos"
                    placeholder="Contrato N°"
                    options={contratos}
                    value={selectedContracts}
                    isLoading={isContractsLoading}
                    onChange={changes =>
                      handleSelectedContractsChange(changes as SelectItem[])
                    }
                    defaultStyle
                    onInputChange={change => {
                      handleBuscaContratoChange(change);
                    }}
                    noOptionsText="Não há opções"
                    isDisabled={isFetching || modoVisualizacao()}
                  />
                )}
                <FormControl isInvalid={errors?.observacoes}>
                  <Textarea
                    name="observacoes"
                    label="Observação"
                    resize="none"
                    error={errors?.observacoes}
                    value={observacoes}
                    onChange={e => handleObservacoes(e)}
                    fontSize="15px"
                    placeholder="Entre com as observações"
                    isDisabled={isFetching || modoVisualizacao()}
                  />
                </FormControl>
              </Stack>
            </Stack>
            {!modoVisualizacao() && (
              <Button
                type="submit"
                onClick={handleSubmit(handleCreate)}
                color="white"
                background="gray.900"
                _hover={{
                  bgColor: 'black',
                }}
                style={{ marginTop: '20px' }}
                isDisabled={isFetching}
              >
                {isEdit ? 'Editar' : 'Cadastrar'}{' '}
                {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
              </Button>
            )}
            {!modoVisualizacao() && (
              <Button
                type="submit"
                onClick={handleClose}
                color="black"
                background="transparent"
                style={{
                  marginTop: '20px',
                  marginLeft: '10px',
                }}
                isDisabled={isFetching}
              >
                Cancelar
              </Button>
            )}
            <Text
              style={{ fontSize: '12px', color: 'gray.500', marginTop: '20px' }}
            >
              * Campos obrigatórios
            </Text>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
